import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Upload, Key, FileText, Send, Eye, EyeOff, Loader2, Paperclip } from "lucide-react";

import { API_URL } from "@/utils/apiConfig";
const API = API_URL;

export default function ClientPortalPage() {
  const { reviewId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessValues, setAccessValues] = useState({});
  const [revealedAccess, setRevealedAccess] = useState(null);
  const [revealTimer, setRevealTimer] = useState(0);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data: d } = await axios.get(`${API}/portal/${reviewId}`);
        setData(d);
      } catch {
        setError("Ce lien n'est pas valide ou a expire.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reviewId]);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const provideAccess = async (accessId) => {
    const value = accessValues[accessId];
    if (!value?.trim()) return;
    try {
      await axios.post(`${API}/portal/${reviewId}/provide-access`, { access_id: accessId, value });
      // Reload
      const { data: d } = await axios.get(`${API}/portal/${reviewId}`);
      setData(d);
      setAccessValues(prev => ({ ...prev, [accessId]: "" }));
    } catch {
      // ignore
    }
  };

  const startReveal = (accessId) => {
    setRevealedAccess(accessId);
    setRevealTimer(30);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setRevealTimer(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          setRevealedAccèss(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadTarget) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      await axios.post(`${API}/portal/${reviewId}/upload-content`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Reload
      const { data: d } = await axios.get(`${API}/portal/${reviewId}`);
      setData(d);
    } catch {
      // ignore
    } finally {
      setUploading(false);
      setUploadTarget(null);
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><Loader2 size={24} className="animate-spin text-indigo-500" /></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><p className="text-sm text-rose-500">{error}</p></div>;

  const totalContent = data?.content_summary?.reduce((acc, g) => acc + g.items.length, 0) || 0;
  const doneContent = data?.content_summary?.reduce((acc, g) => acc + g.items.filter(i => i.done).length, 0) || 0;
  const contentProgress = totalContent > 0 ? Math.round((doneContent / totalContent) * 100) : 0;

  const totalAccesses = data?.client_accesses?.length || 0;
  const providedAccesses = data?.client_accesses?.filter(a => a.status === "provided").length || 0;

  return (
    <div className="min-h-screen bg-gray-50/50" data-testid="client-portal">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <div>
            <h1 className="font-bold text-sm text-gray-900">{data?.title || "Projet"}</h1>
            <span className="text-[10px] text-gray-400">Portail client — Suivi et livraison</span>
          </div>
          <span className="text-[10px] font-semibold text-indigo-600 bg-indigo-50 px-3 py-1 rounded-full border border-indigo-200">CardFlow</span>
        </div>
      </header>

      <div className="max-w-4xl mx-auto p-6 md:p-8 space-y-6">
        {/* Progress overview */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={16} className="text-indigo-600" />
              <h3 className="font-semibold text-sm text-gray-900">Contenus a fournir</h3>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl font-bold font-mono text-gray-900">{doneContent}/{totalContent}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${contentProgress === 100 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{contentProgress}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${contentProgress}%`, backgroundColor: contentProgress === 100 ? "#10B981" : "#6366F1" }} />
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <Key size={16} className="text-violet-600" />
              <h3 className="font-semibold text-sm text-gray-900">Accès a fournir</h3>
            </div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-2xl font-bold font-mono text-gray-900">{providedAccèsses}/{totalAccèsses}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${providedAccèsses === totalAccèsses && totalAccèsses > 0 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                {totalAccèsses === 0 ? "Aucun" : providedAccèsses === totalAccèsses ? "Complet" : "En attente"}
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full rounded-full transition-all bg-violet-500" style={{ width: totalAccèsses > 0 ? `${(providedAccèsses / totalAccèsses) * 100}%` : "0%" }} />
            </div>
          </div>
        </div>

        {/* Accèsses to provide */}
        {data?.client_accesses?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-sm text-gray-900 flex items-center gap-2"><Key size={14} className="text-violet-600" /> Accès et clés API a fournir</h2>
              <p className="text-[10px] text-gray-400 mt-1">Renseignez les accès demandes par le prestataire. Les valeurs sont chiffrees et masquees.</p>
            </div>
            <div className="divide-y divide-gray-50">
              {data.client_accesses.map(acc => (
                <div key={acc.id} className="px-5 py-4 flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-sm text-gray-900">{acc.label}</span>
                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 uppercase">{acc.access_type}</span>
                    </div>
                    {acc.status === "provided" ? (
                      <span className="flex items-center gap-1 text-xs text-emerald-600 mt-1"><CheckCircle size={11} /> Fourni</span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-600 mt-1"><XCircle size={11} /> En attente</span>
                    )}
                  </div>
                  {acc.status !== "provided" ? (
                    <div className="flex gap-1.5 items-center">
                      <input
                        type="password"
                        value={accessValues[acc.id] || ""}
                        onChange={e => setAccèssValues(prev => ({ ...prev, [acc.id]: e.target.value }))}
                        placeholder="Votre cle / accès..."
                        className="w-56 rounded-lg border border-gray-200 px-3 py-2 text-xs focus:outline-none focus:border-violet-400"
                        data-testid={`access-input-${acc.id}`}
                      />
                      <button
                        onClick={() => provideAccèss(acc.id)}
                        disabled={!accessValues[acc.id]?.trim()}
                        className="bg-violet-600 text-white px-3 py-2 rounded-lg text-xs font-semibold hover:bg-violet-700 disabled:opacity-30 flex items-center gap-1"
                        data-testid={`access-submit-${acc.id}`}
                      >
                        <Send size={11} /> Envoyer
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => startReveal(acc.id)}
                      className="text-xs text-gray-400 hover:text-violet-600 flex items-center gap-1 border border-gray-200 px-2.5 py-1.5 rounded-lg"
                    >
                      {revealedAccèss === acc.id ? (
                        <><Eye size={11} /> {revealTimer}s</>
                      ) : (
                        <><EyeOff size={11} /> Voir</>
                      )}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Content items */}
        {data?.content_summary?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="font-semibold text-sm text-gray-900 flex items-center gap-2"><FileText size={14} className="text-indigo-600" /> Contenus attendus par composant</h2>
              <p className="text-[10px] text-gray-400 mt-1">Voici la liste des contenus necessaires pour chaque partie du projet.</p>
            </div>
            <div className="divide-y divide-gray-50">
              {data.content_summary.map((group, gi) => (
                <div key={gi} className="px-5 py-4">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-sm text-gray-900">{group.node_title}</h3>
                    {group.page_title && <span className="text-[9px] text-violet-600 bg-violet-50 px-1.5 py-0.5 rounded-full">{group.page_title}</span>}
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ml-auto ${
                      group.items.every(i => i.done) ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                    }`}>
                      {group.items.filter(i => i.done).length}/{group.items.length}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {group.items.map((item, ii) => (
                      <div key={ii} className="flex items-center gap-2 text-xs">
                        {item.done ? (
                          <CheckCircle size={14} className="text-emerald-500 shrink-0" />
                        ) : (
                          <XCircle size={14} className="text-gray-300 shrink-0" />
                        )}
                        <span className={item.done ? "text-gray-400 line-through" : "text-gray-700"}>{item.label}</span>
                        {!item.done && (
                          <button
                            onClick={() => { setUploadTarget({ nodeId: group.node_id, label: item.label }); fileInputRef.current?.click(); }}
                            className="ml-auto text-indigo-500 hover:text-indigo-700 flex items-center gap-1 text-[10px] bg-indigo-50 px-2 py-0.5 rounded-full"
                          >
                            <Upload size={10} /> {uploading ? "..." : "Fournir"}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <input ref={fileInputRef} type="file" onChange={handleUpload} className="hidden" />

        {/* Review link */}
        <div className="text-center py-4">
          <a href={`/review/${reviewId}`} className="text-xs text-indigo-600 hover:text-indigo-800 underline flex items-center gap-1 justify-center">
            <Eye size={12} /> Voir la présentation du projet
          </a>
        </div>
      </div>

      <footer className="border-t border-gray-100 py-6 px-6 text-center">
        <span className="text-[10px] text-gray-400">Portail sécurisé — CardFlow {new Date().getFullYear()}</span>
      </footer>
    </div>
  );
}
